"use client";

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getProjects, getProjectTags, ProjectListItem, ProjectTag } from '../api/projects';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Button } from '../ui/components/Button';
import { TextField } from '../ui/components/TextField';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { formatPrice } from '../utils/formatters';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import {
  FeatherSearch,
  FeatherChevronDown,
  FeatherExternalLink,
  FeatherTrendingUp,
  FeatherTrendingDown,
} from '@subframe/core';

type SortOption = 'alphabetical' | 'market_cap' | 'floor_value' | 'collection_count' | 'recent';

const sortOptions: { value: SortOption; label: string }[] = [
  { value: 'alphabetical', label: 'Alphabetical' },
  { value: 'floor_value', label: 'Total Floor Value' },
  { value: 'collection_count', label: 'Collection Count' },
  { value: 'recent', label: 'Recently Added' },
];

export const ProjectsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params
  const initSortOption = (() => {
    const sort = searchParams.get('sort') as SortOption;
    return sort && sortOptions.some(opt => opt.value === sort) ? sort : 'alphabetical';
  })();
  const initSearchQuery = searchParams.get('q') || '';
  const initSelectedTags = searchParams.get('tags')?.split(',').filter(Boolean) || [];

  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [tags, setTags] = useState<ProjectTag[]>([]);
  const [tagsLoading, setTagsLoading] = useState(true);
  const [tagsError, setTagsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortOption, setSortOption] = useState<SortOption>(initSortOption);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState(initSearchQuery);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(initSearchQuery);
  const [selectedTags, setSelectedTags] = useState<string[]>(initSelectedTags);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch tags on mount
  const fetchTags = useCallback(async () => {
    try {
      setTagsLoading(true);
      setTagsError(null);
      const fetchedTags = await getProjectTags();
      setTags(fetchedTags);
    } catch (err) {
      console.error('Failed to load project tags:', err);
      setTagsError('Failed to load filters');
    } finally {
      setTagsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Update URL when filters change
  const updateURLParams = useCallback((params: {
    sort?: SortOption;
    q?: string;
    tags?: string[];
  }) => {
    const newParams = new URLSearchParams(searchParams);

    if (params.sort !== undefined) {
      if (params.sort === 'alphabetical') {
        newParams.delete('sort');
      } else {
        newParams.set('sort', params.sort);
      }
    }

    if (params.q !== undefined) {
      if (params.q) {
        newParams.set('q', params.q);
      } else {
        newParams.delete('q');
      }
    }

    if (params.tags !== undefined) {
      if (params.tags.length > 0) {
        newParams.set('tags', params.tags.join(','));
      } else {
        newParams.delete('tags');
      }
    }

    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  const fetchProjects = useCallback(async (page: number, append: boolean = false) => {
    if (page === 1) {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await getProjects({
        search: debouncedSearchQuery || undefined,
        tags: selectedTags.length > 0 ? selectedTags : undefined,
        sortBy: sortOption,
        sortOrder: sortOption === 'alphabetical' ? 'asc' : 'desc',
        page,
        limit: 24,
      });

      if (append) {
        setProjects(prev => [...prev, ...response.projects]);
      } else {
        setProjects(response.projects);
      }

      setTotalPages(response.pagination.pages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, [sortOption, debouncedSearchQuery, selectedTags]);

  // Fetch projects when filters change
  useEffect(() => {
    setCurrentPage(1);
    fetchProjects(1, false);
  }, [sortOption, debouncedSearchQuery, selectedTags, fetchProjects]);

  // Handle sort change
  const handleSortChange = (newSort: SortOption) => {
    setSortOption(newSort);
    setShowSortDropdown(false);
    updateURLParams({ sort: newSort });
  };

  // Handle search
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    updateURLParams({ q: value });
  };

  // Handle tag toggle
  const handleTagToggle = (tagSlug: string) => {
    const newTags = selectedTags.includes(tagSlug)
      ? selectedTags.filter(t => t !== tagSlug)
      : [...selectedTags, tagSlug];
    setSelectedTags(newTags);
    updateURLParams({ tags: newTags });
  };

  // Load more projects
  const loadMore = useCallback(() => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchProjects(nextPage, true);
    }
  }, [currentPage, totalPages, fetchProjects]);

  // Infinite scroll
  const sentinelRef = useInfiniteScroll({
    onLoadMore: loadMore,
    enabled: currentPage < totalPages && !loading && projects.length > 0,
  });

  const getCurrentSortLabel = (): string => {
    return sortOptions.find(opt => opt.value === sortOption)?.label || 'Alphabetical';
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (showSortDropdown) {
        setShowSortDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showSortDropdown]);

  // Format price change with color and icon
  const formatPriceChange = (change: number | null | undefined) => {
    if (change === null || change === undefined) return null;
    const isPositive = change >= 0;
    const Icon = isPositive ? FeatherTrendingUp : FeatherTrendingDown;
    const colorClass = isPositive ? 'text-success-600' : 'text-error-600';
    return (
      <span className={`flex items-center gap-1 ${colorClass}`}>
        <Icon className="w-3 h-3" />
        {isPositive ? '+' : ''}{change.toFixed(2)}%
      </span>
    );
  };

  // Format USD values with $ prefix and K/M suffix
  const formatUsd = (num: number | null | undefined): string => {
    if (num === null || num === undefined) return '--';
    if (num >= 1_000_000_000) return `$${(num / 1_000_000_000).toFixed(2)}B`;
    if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
    if (num >= 1_000) return `$${(num / 1_000).toFixed(2)}K`;
    return `$${num.toFixed(2)}`;
  };

  // Format large numbers with K/M/B suffix
  const formatNumber = (num: number | null | undefined): string => {
    if (num === null || num === undefined) return '--';
    if (num >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(2)}B`;
    if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(2)}M`;
    if (num >= 1_000) return `${(num / 1_000).toFixed(2)}K`;
    return num.toLocaleString();
  };

  return (
    <DefaultPageLayout>
      <div className="container max-w-none flex h-full w-full flex-col items-start gap-8 bg-default-background py-8 overflow-auto mobile:gap-4 mobile:py-4">
        {/* Page Header */}
        <div className="flex w-full flex-col items-start gap-2">
          <h1 className="text-heading-1 font-heading-1 text-default-font mobile:text-heading-2 mobile:font-heading-2">
            Projects
          </h1>
          <p className="text-body font-body text-subtext-color mobile:text-caption mobile:font-caption">
            Discover unified project hubs combining NFT collections with their associated tokens
          </p>
        </div>

        {/* Controls Bar */}
        <div className="flex w-full items-center gap-3 mobile:flex-col mobile:items-stretch mobile:gap-2">
          {/* Search */}
          <TextField
            className="h-auto w-72 flex-none mobile:w-full"
            variant="filled"
            label=""
            helpText=""
            icon={<FeatherSearch />}
          >
            <TextField.Input
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                handleSearchChange(event.target.value);
              }}
            />
          </TextField>

          {/* Sort Dropdown */}
          <div className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="neutral-secondary"
              iconRight={<FeatherChevronDown />}
              onClick={() => setShowSortDropdown(!showSortDropdown)}
            >
              {getCurrentSortLabel()}
            </Button>

            {showSortDropdown && (
              <div className="absolute top-full left-0 mt-2 w-48 rounded-md border border-solid border-neutral-border bg-default-background shadow-lg z-20">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleSortChange(option.value)}
                    className={`w-full text-left px-4 py-2 text-body font-body hover:bg-neutral-100 transition-colors ${
                      sortOption === option.value ? 'bg-brand-100 text-brand-700' : 'text-default-font'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Spacer */}
          <div className="flex-1 mobile:hidden" />
        </div>

        {/* Tag Filters */}
        <div className="flex w-full items-center gap-2 flex-wrap min-h-[32px]">
          <span className="text-caption font-caption text-subtext-color">Tags:</span>
          {tagsLoading ? (
            <span className="text-caption font-caption text-subtext-color">Loading...</span>
          ) : tagsError ? (
            <button
              onClick={fetchTags}
              className="text-caption font-caption text-error-600 hover:text-error-700 underline"
            >
              {tagsError} - Click to retry
            </button>
          ) : tags.length === 0 ? (
            <span className="text-caption font-caption text-subtext-color">No tags available</span>
          ) : (
            <>
              {tags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => handleTagToggle(tag.slug)}
                  className={`px-3 py-1 rounded-full text-caption font-caption transition-colors ${
                    selectedTags.includes(tag.slug)
                      ? 'bg-brand-600 text-white'
                      : 'bg-neutral-100 text-default-font hover:bg-neutral-200'
                  }`}
                >
                  {tag.name}
                </button>
              ))}
              {selectedTags.length > 0 && (
                <button
                  onClick={() => {
                    setSelectedTags([]);
                    updateURLParams({ tags: [] });
                  }}
                  className="px-3 py-1 rounded-full text-caption font-caption text-subtext-color hover:text-default-font transition-colors"
                >
                  Clear all
                </button>
              )}
            </>
          )}
        </div>

        {/* Content */}
        {loading && currentPage === 1 ? (
          <div className="flex w-full items-center justify-center py-16">
            <div className="flex flex-col items-center gap-4">
              <LoadingSpinner size="md" />
              <span className="text-body font-body text-subtext-color">Loading projects...</span>
            </div>
          </div>
        ) : error ? (
          <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-default-background p-12">
            <div className="flex flex-col items-center gap-4">
              <span className="text-body font-body text-error">{error}</span>
              <Button variant="brand-secondary" onClick={() => fetchProjects(1, false)}>
                Try Again
              </Button>
            </div>
          </div>
        ) : projects.length === 0 ? (
          <div className="flex w-full items-center justify-center rounded-md border border-solid border-neutral-border bg-default-background p-12">
            <div className="flex flex-col items-center gap-4">
              <span className="text-heading-3 font-heading-3 text-default-font">No projects found</span>
              <span className="text-body font-body text-subtext-color">
                {searchQuery || selectedTags.length > 0
                  ? 'Try adjusting your search or filters'
                  : 'No projects are available yet'}
              </span>
              {(searchQuery || selectedTags.length > 0) && (
                <Button
                  variant="brand-secondary"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedTags([]);
                    updateURLParams({ q: '', tags: [] });
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Projects Grid */}
            <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {projects.map((project) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.slug}`}
                  className="group flex flex-col rounded-lg border border-solid border-neutral-border bg-default-background overflow-hidden hover:border-brand-500 hover:shadow-lg transition-all"
                >
                  {/* Project Image */}
                  <div className="relative aspect-square bg-neutral-900 overflow-hidden">
                    {project.image_url ? (
                      <img
                        src={project.image_url}
                        alt={project.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-heading-2 font-heading-2 text-neutral-400">
                          {project.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Project Info */}
                  <div className="flex flex-col gap-3 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-body-bold font-body-bold text-default-font line-clamp-1">
                        {project.name}
                      </h3>
                    </div>

                    {/* Tags - fixed height for card alignment */}
                    <div className="flex flex-wrap gap-1 min-h-[24px]">
                      {project.tags && project.tags.length > 0 ? (
                        project.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag.id}
                            className="px-2 py-0.5 rounded-full bg-neutral-100 text-caption font-caption text-subtext-color"
                          >
                            {tag.name}
                          </span>
                        ))
                      ) : (
                        <span className="text-caption font-caption text-neutral-400 italic">No tags</span>
                      )}
                    </div>

                    {/* NFTs Section */}
                    <div className="flex flex-col gap-2 border-t border-neutral-border pt-3">
                      <span className="text-caption font-caption text-subtext-color font-semibold text-center">NFTs</span>
                      <div className="flex items-center justify-between text-caption font-caption">
                        <span className="text-subtext-color">Collections:</span>
                        <span className="text-default-font">{project.collection_count || 0}</span>
                      </div>
                      <div className="flex items-center justify-between text-caption font-caption">
                        <span className="text-subtext-color">Total NFTs:</span>
                        <span className="text-default-font">
                          {formatNumber(project.total_nft_count || 0)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-caption font-caption">
                        <span className="text-subtext-color">24h Volume:</span>
                        <span className="text-default-font">
                          {formatNumber(project.total_volume_24h || 0)} XRP
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-caption font-caption">
                        <span className="text-subtext-color">Total Volume:</span>
                        <span className="text-default-font">
                          {formatNumber(project.total_volume || 0)} XRP
                        </span>
                      </div>
                    </div>

                    {/* Token Section */}
                    <div className="flex flex-col gap-2 border-t border-neutral-border pt-3">
                      <span className="text-caption font-caption text-subtext-color font-semibold text-center">
                        {project.primary_token && (project.token_count ?? 1) > 1 ? 'Primary Token' : 'Token'}
                      </span>
                      {project.primary_token && project.primary_token.has_liquidity ? (
                        <>
                          <div className="flex items-center justify-between text-caption font-caption">
                            <span className="text-subtext-color">Name:</span>
                            <span className="text-default-font font-medium">
                              ${project.primary_token.display_name || project.primary_token.currency_code}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-caption font-caption">
                            <span className="text-subtext-color">Market Cap:</span>
                            <span className="text-default-font">
                              {project.primary_token.market_cap_usd != null
                                ? formatUsd(project.primary_token.market_cap_usd)
                                : '--'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-caption font-caption">
                            <span className="text-subtext-color">Holders:</span>
                            <span className="text-default-font">
                              {project.primary_token.holder_count != null
                                ? formatNumber(project.primary_token.holder_count)
                                : '--'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-caption font-caption">
                            <span className="text-subtext-color">Liquidity:</span>
                            <span className="text-default-font">
                              {project.primary_token.amm_liquidity_usd != null
                                ? formatUsd(project.primary_token.amm_liquidity_usd)
                                : '--'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between text-caption font-caption">
                            <span className="text-subtext-color">24h Volume:</span>
                            <span className="text-default-font">
                              {project.primary_token.volume_24h_xrp != null
                                ? `${formatNumber(project.primary_token.volume_24h_xrp)} XRP`
                                : '--'}
                            </span>
                          </div>
                        </>
                      ) : (
                        <span className="text-caption font-caption text-neutral-400 italic text-center py-2">
                          No token associated with project
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Load More Sentinel */}
            {currentPage < totalPages && (
              <div ref={sentinelRef} className="w-full flex justify-center py-8">
                {loading && <LoadingSpinner size="sm" />}
              </div>
            )}
          </>
        )}
      </div>
    </DefaultPageLayout>
  );
};
